import React, { useState, useEffect, FormEvent } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  addNewResident,
  editResidentStepOne,
  addNextOfKin,
} from '@api/resident';
import useAuth from '@hooks/useAuth';
import {
  AddNewResidentTypes,
  ResidentDetails,
  ResidentPageType,
  StepChangeDirection,
} from '@typings/ResidentTypes';
import { ApiResponse } from '@typings/ApiResponse';
import { Role } from '@typings/ProfileTypes';
import { FormError, SubmitStatus, SubmitStatusEnum } from '@typings/common';
import { updateErrors, isValidEmail } from '@utils/commons';
import { CarehomeAddressType } from '@typings/CarehomeInfo';
import dayjs from 'dayjs';
import { ErrorsProvider, useErrors } from '@contexts/ErrorsContext';
import SuccessNotificationBanner from '@components/SuccessNotificationBanner';
import UploadImage from '@components/upload-image';
import NewErrorSummary from '@components/NewErrorSummary';
import DateOfBirth from './components/DateOfBirth';
import ContactDetails, {
  ContactDetailsTypes,
} from './components/contact-details';
import OtherContactDetails from './components/other-contact-details/index';
import Address from './components/address';
import FullName from './components/FullName';
import NhsNumber, { validateNhsNumber } from './components/NhsNumber';
import GeneralPractice, {
  GeneralPracticeType,
} from './components/GeneralPractice';
import OrganDonation from './components/OrganDonation';
import {
  validateDobMessage,
  nokStatusMap,
  resetNextOfKinDetails,
} from './utils';
import { NextOfKinDetailsType } from './components/NextOfKinDetails';

interface StepOneProps {
  role: string;
  type: ResidentPageType;
  changeStep: (status: StepChangeDirection) => void;
  // eslint-disable-next-line react/require-default-props
  residentDetails?: ResidentDetails;
}

const StepOneComponent: React.FC<StepOneProps> = ({
  role,
  type,
  changeStep,
  residentDetails,
}) => {
  const [bio, setBio] = useState('');
  const [residentAvatar, setResidentAvatar] = useState<string>('');
  const [fullName, setFullName] = useState('');
  const [dateOfBirth, setDateOfbirth] = useState<string>('');
  const { errors, setErrors } = useErrors();
  const [contactDetails, setContactDetails] = useState<ContactDetailsTypes>({
    mobileNumber: '',
    homeNumber: '',
    email: '',
    preferredMethod: '',
  });
  const [selectedDate, setSelectedDate] = useState({
    day: '',
    month: '',
    year: '',
  });
  const { partnerId } = useAuth();
  const { id: slug } = useParams<{ id: string }>();
  const [otherDetails, setOtherDetails] = useState({
    details: '',
    maritalStatus: '',
  });
  const [stepOneApiStatus, setStepOneApiStatus] = useState<SubmitStatus>({
    loading: false,
    submitted: SubmitStatusEnum.IDLE,
    message: '',
  });
  const [address, setAddress] = useState({
    isResideInCurrentAddress: false,
    currentAddressType: '',
    alternativeAddressType: '',
    currentAddress: {
      addressLine1: '',
      addressLine2: '',
      city: '',
      county: '',
      postCode: '',
    },
    alternativeAddress: {
      addressLine1: '',
      addressLine2: '',
      city: '',
      county: '',
      postCode: '',
    },
  });
  const [addressFromData, setAddressFromData] = useState({
    isResideInCurrentAddress: false,
    currentAddressType: '',
    alternativeAddressType: '',
    currentAddress: {
      addressLine1: '',
      addressLine2: '',
      city: '',
      county: '',
      postCode: '',
    },
    alternativeAddress: {
      addressLine1: '',
      addressLine2: '',
      city: '',
      county: '',
      postCode: '',
    },
  });

  const [nhsNumber, setNhsNumber] = useState('');
  const [generalPractice, setGeneralPractice] = useState<GeneralPracticeType>({
    gpName: '',
    gpPracticeName: '',
  });
  const [gpPracticeAddress, setGpPracticeAddress] =
    useState<CarehomeAddressType>({
      addressLine1: '',
      addressLine2: '',
      city: '',
      county: '',
      postCode: '',
    });
  const [gpPracticeAddressFromData, setGpPracticeAddressFromData] =
    useState<CarehomeAddressType>({
      addressLine1: '',
      addressLine2: '',
      city: '',
      county: '',
      postCode: '',
    });

  const [nextOfKinDetails, setNextOfKinDetails] =
    useState<NextOfKinDetailsType>({
      nextOfKinStatus: '',
      nfkFullName: '',
      nfkPhoneNumber: '',
      nfkRelationship: '',
      haveLPA: '',
    });
  const [organDonation, setOrganDonation] = useState<boolean | null>(null);
  const navigate = useNavigate();

  const getNfkStatus = (status: boolean) => {
    if (status === true) {
      return 'yes';
    }
    if (status === false) {
      return 'no';
    }
    return '';
  };

  useEffect(() => {
    if (residentDetails) {
      const { profile, avatar } = residentDetails;
      setFullName(profile.full_name);
      setBio(profile.bio || '');
      setResidentAvatar(avatar || '');
      setContactDetails({
        mobileNumber: profile.phone,
        homeNumber: profile.home_phone,
        email: profile.preferred_email,
        preferredMethod: profile.preferred_method_of_contact,
      });
      setOtherDetails({
        details: profile.other_contact,
        maritalStatus: profile.marital_status,
      });
      setNextOfKinDetails({
        nextOfKinStatus: getNfkStatus(profile.is_next_of_kin),
        haveLPA: profile.next_of_kin_lpa_for_health_welfare,
        nfkFullName: profile.nfk_name,
        nfkPhoneNumber: profile.nfk_phone,
        nfkRelationship: profile.nfk_relationship,
      });

      setAddressFromData({
        isResideInCurrentAddress: profile.is_reside,
        currentAddressType: profile.current_address_type,
        alternativeAddressType: profile.alternative_address_type,
        currentAddress: {
          addressLine1: profile.current_address_line1,
          addressLine2: profile.current_address_line2,
          city: profile.current_address_city,
          county: profile.current_address_county,
          postCode: profile.current_address_postcode,
        },
        alternativeAddress: {
          addressLine1: profile.alternative_address_line1,
          addressLine2: profile.alternative_address_line2,
          city: profile.alternative_address_city,
          county: profile.alternative_address_county,
          postCode: profile.alternative_address_postcode,
        },
      });
      setNhsNumber(profile.nhs_number);
      setGeneralPractice({
        gpName: profile.gp_name,
        gpPracticeName: profile.gp_practice_name,
      });
      setGpPracticeAddressFromData({
        addressLine1: profile.gp_address_line1,
        addressLine2: profile.gp_address_line2,
        city: profile.gp_address_city,
        county: profile.gp_address_county,
        postCode: profile.gp_address_postcode,
      });
      setOrganDonation(profile.organ_tissues_donation);
    }
  }, [residentDetails, setFullName]);

  const handleRedirect = (newSlug: string) => {
    const scrollPosition = window.scrollY;
    navigate(`/patient/${newSlug}`, { replace: true });

    setTimeout(() => {
      window.scrollTo(0, scrollPosition);
    }, 5);
  };

  const handleAddNewResident = async (residentData: AddNewResidentTypes) => {
    setStepOneApiStatus(prev => ({ ...prev, loading: true }));
    try {
      const response: ApiResponse<ResidentDetails> =
        await addNewResident(residentData);

      if (response && response.status === 'success') {
        setStepOneApiStatus(prev => ({
          ...prev,
          submitted: SubmitStatusEnum.SUCCESS,
          message:
            'You can continue editing this page or move to the next section.',
        }));

        setTimeout(() => {
          setStepOneApiStatus(prev => ({
            ...prev,
            submitted: SubmitStatusEnum.IDLE,
            message: '',
          }));
          handleRedirect(response.data.slug);
        }, 3000);
      }
    } catch (error: any) {
      console.error(error);
    } finally {
      setStepOneApiStatus(prev => ({ ...prev, loading: false }));
    }
  };

  const handleEditResident = async (residentData: AddNewResidentTypes) => {
    if (slug) {
      setStepOneApiStatus(prev => ({ ...prev, loading: true }));
      try {
        const response = await editResidentStepOne(slug, residentData);
        if (response && response.status === 'success') {
          if (slug !== response.data.slug) {
            handleRedirect(response.data.slug);
          }

          setStepOneApiStatus(prev => ({
            ...prev,
            submitted: SubmitStatusEnum.SUCCESS,
            message:
              'You can continue editing this page or move to the next section.',
          }));
          setTimeout(() => {
            setStepOneApiStatus(prev => ({
              ...prev,
              submitted: SubmitStatusEnum.IDLE,
              message: '',
            }));
          }, 3000);
        }
      } catch (error: any) {
        console.error(error);
      } finally {
        setStepOneApiStatus(prev => ({ ...prev, loading: false }));
      }
    }
  };

  const addNextOfKinFunction = () => {
    let newNextOfKinDetails = { ...nextOfKinDetails };

    if (nextOfKinDetails.nextOfKinStatus === 'no') {
      newNextOfKinDetails = {
        ...nextOfKinDetails,
        nfkFullName: '',
        nfkRelationship: '',
        nfkPhoneNumber: '',
      };
      setNextOfKinDetails(newNextOfKinDetails);
    }

    if (nextOfKinDetails.nextOfKinStatus === 'yes' && slug) {
      const handleAddNextOfKin = async () => {
        await addNextOfKin(
          slug,
          newNextOfKinDetails.nfkFullName,
          newNextOfKinDetails.nfkRelationship
        );
      };
      handleAddNextOfKin();
    }
  };

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    let validationErrors: FormError[] = [];
    validationErrors = updateErrors(
      validationErrors,
      'fullName',
      !fullName ? "Enter the person's full name" : null
    );

    const dobError = validateDobMessage(selectedDate);
    validationErrors = updateErrors(validationErrors, 'dateOfBirth', dobError);

    let formattedDate = dateOfBirth;
    if (!dobError) {
      formattedDate = dayjs(
        `${selectedDate.year}-${selectedDate.month}-${selectedDate.day}`
      ).format('YYYY-MM-DD');
      setDateOfbirth(formattedDate);
    }

    validationErrors = updateErrors(
      validationErrors,
      'preferredContactEmail',
      !isValidEmail(contactDetails.email) ? 'Please enter a valid email' : null
    );

    validationErrors = updateErrors(
      validationErrors,
      'nhsNumber',
      validateNhsNumber(nhsNumber)
    );

    if (nextOfKinDetails.nfkFullName && !nextOfKinDetails.nfkRelationship) {
      validationErrors = updateErrors(
        validationErrors,
        'nfkRelationship',
        'Please enter relationship'
      );
    }
    if (nextOfKinDetails.nfkRelationship && !nextOfKinDetails.nfkFullName) {
      validationErrors = updateErrors(
        validationErrors,
        'nfkFullName',
        'Please enter full name'
      );
    }

    setErrors(validationErrors);

    if (validationErrors.length !== 0) {
      return;
    }
    addNextOfKinFunction();
    const { mobileNumber, homeNumber, email, preferredMethod } = contactDetails;
    const {
      nextOfKinStatus,
      haveLPA,
      nfkFullName,
      nfkPhoneNumber,
      nfkRelationship,
    } = resetNextOfKinDetails(nextOfKinDetails);

    const residentData: any = {
      full_name: fullName,
      bod: formattedDate,
      partner_carehome_id: partnerId!,
      avatar: residentAvatar,
      phone: mobileNumber,
      home_phone: homeNumber,
      preferred_email: email,
      preferred_method_of_contact: preferredMethod,
      other_contact: otherDetails.details,
      marital_status: otherDetails.maritalStatus,
      is_reside: address.isResideInCurrentAddress,
      current_address_type: address.currentAddressType,
      alternative_address_type: address.alternativeAddressType,
      current_address_line1: address.currentAddress.addressLine1,
      current_address_line2: address.currentAddress.addressLine2,
      current_address_city: address.currentAddress.city,
      current_address_county: address.currentAddress.county,
      current_address_postcode: address.currentAddress.postCode,
      alternative_address_line1: address.alternativeAddress.addressLine1,
      alternative_address_line2: address.alternativeAddress.addressLine2,
      alternative_address_city: address.alternativeAddress.city,
      alternative_address_county: address.alternativeAddress.county,
      alternative_address_postcode: address.alternativeAddress.postCode,
      nhs_number: nhsNumber,
      organ_tissues_donation: organDonation,
      gp_name: generalPractice.gpName,
      gp_practice_name: generalPractice.gpPracticeName,
      gp_address_line1: gpPracticeAddress.addressLine1,
      gp_address_line2: gpPracticeAddress.addressLine2,
      gp_address_city: gpPracticeAddress.city,
      gp_address_county: gpPracticeAddress.county,
      gp_address_postcode: gpPracticeAddress.postCode,
      bio,
      is_next_of_kin:
        nextOfKinStatus === 'yes' || nextOfKinStatus === 'no'
          ? nokStatusMap[nextOfKinStatus]
          : null,
      nfk_name: nfkFullName,
      nfk_phone: nfkPhoneNumber,
      nfk_relationship: nfkRelationship,
      next_of_kin_lpa_for_health_welfare: haveLPA,
    };

    if (type === ResidentPageType.ADD) {
      handleAddNewResident(residentData);
    } else {
      handleEditResident(residentData);
    }
  };

  return (
    <div className="mt-5 md:mt-10 step-1">
      <h1 className="govuk-heading-xl">Add / edit details</h1>
      <div className="step1-upload">
        <UploadImage
          isEditable={role !== Role.TeamMemberViewOnly}
          isCustomized
          title="Add a recent profile photo"
          imageUrl={residentAvatar}
          setImageUrl={setResidentAvatar}
        />
      </div>
      <form onSubmit={e => handleSubmit(e)}>
        {errors.length > 0 && <NewErrorSummary errors={errors} />}

        <FullName fullName={fullName} setFullName={setFullName} />
        <DateOfBirth
          bod={residentDetails ? residentDetails.profile.bod : ''}
          selectedDate={selectedDate}
          setSelectedDate={setSelectedDate}
        />
        <ContactDetails
          contactDetails={contactDetails}
          setContactDetails={setContactDetails}
        />
        <OtherContactDetails
          otherDetails={otherDetails}
          setOtherDetails={setOtherDetails}
        />
        <Address
          fullName={fullName}
          setAddress={setAddress}
          addressFromData={addressFromData}
          nextOfKinDetails={nextOfKinDetails}
          setNextOfKinDetails={setNextOfKinDetails}
        />

        <NhsNumber setNhsNumber={setNhsNumber} nhsNumber={nhsNumber} />
        <GeneralPractice
          generalPractice={generalPractice}
          setGeneralPractice={setGeneralPractice}
          gpPracticeAddress={gpPracticeAddress}
          setGpPracticeAddress={setGpPracticeAddress}
          gpPracticeAddressFromData={gpPracticeAddressFromData}
        />
        <OrganDonation
          organDonation={organDonation}
          setOrganDonation={setOrganDonation}
        />

        <div className="govuk-form-group mt-10 govuk-bp:mt-14">
          <h1 className="govuk-label-wrapper">
            <label
              className="govuk-label govuk-label--l"
              htmlFor="short-biography"
            >
              Would you like to provide a short biography to me?
            </label>
          </h1>
          <textarea
            className="govuk-textarea"
            id="short-biography"
            name="bio"
            rows={5}
            autoComplete="bio"
            value={bio}
            onChange={e => setBio(e.target.value)}
          />
        </div>

        <div className="govuk-button-group govuk-bp:justify-end">
          <button
            type="submit"
            className="govuk-button"
            disabled={stepOneApiStatus.loading}
            aria-disabled={stepOneApiStatus.loading}
          >
            {type === ResidentPageType.ADD ? 'Submit' : 'Save'}
          </button>

          {type === ResidentPageType.EDIT && (
            <button
              type="submit"
              className="govuk-button"
              disabled={stepOneApiStatus.loading}
              onClick={() => changeStep(StepChangeDirection.Next)}
            >
              Next
            </button>
          )}
        </div>
        {stepOneApiStatus.submitted === SubmitStatusEnum.SUCCESS && (
          <SuccessNotificationBanner
            duration={3000}
            title="This document has been saved."
            content={stepOneApiStatus.message}
            noScrollToTop
          />
        )}
      </form>
    </div>
  );
};

StepOneComponent.defaultProps = {
  residentDetails: undefined,
};

const StepOne: React.FC<StepOneProps> = props => {
  return (
    <ErrorsProvider>
      {/* eslint-disable-next-line react/jsx-props-no-spreading */}
      <StepOneComponent {...props} />
    </ErrorsProvider>
  );
};

export default StepOne;
